import { useEffect, useState } from "react";
import api from "../../../../utils/auth/api";
import { decryptPayload, formatDateTime, formatLabel } from "../../../../constants/helpers";
import { toast } from "react-toastify";
import Loader from "../../../../components/globalloaders/Loader";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

const EntitySelector = ({ open, onClose, apiPath, label = "Select", displayFields = [], multi = false, defaultSelected = [], onSelect }) => {

    const [query, setQuery] = useState("");
    const [data, setData] = useState([]);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [shouldSearch, setShouldSearch] = useState(false);
    const [selected, setSelected] = useState([]);

    // Reset state when modal closes
    useEffect(() => {
        if (!open) {
            setQuery("");
            setData([]);
            setPage(1);
            setPages(1);
            setShouldSearch(false);
            setLoading(false);
            setSelected([]);
        }
    }, [open]);
    useEffect(() => {
        if (open && multi && defaultSelected?.length) {
            setSelected(defaultSelected);
        }
    }, [open, defaultSelected, multi]);

    // Toggle item selection
    const toggleSelect = (item) => {
        if (!multi) {
            onSelect(item);
            onClose();
            return;
        }

        setSelected((prev) => {
            const exists = prev.find((i) => i._id === item._id);
            if (exists) return prev.filter((i) => i._id !== item._id);
            return [...prev, item];
        });
    };

    // Fetch data ONLY when user clicks Search or paginates after search
    useEffect(() => {
        if (!open || !shouldSearch || query.length < 2) return;
        const fetchData = async () => {
            setLoading(true);
            try {
                const res = await api.get(
                    `${process.env.REACT_APP_URI}${apiPath}`,
                    {
                        params: {
                            q: query,
                            page,
                            limit: 15,
                        },
                    }
                );
                if (res.data.success) {
                    const decrypted = decryptPayload(res.data.encryptedData);
                    setData(decrypted.data || []);
                    setPages(decrypted.pages || 1);
                } else {
                    toast.error(res.data.message);
                }
            } catch (err) {
                console.error(`Search ${label} error:`, err);
                toast.error(err?.response.data.message || `Unable to search ${label}`);
            } finally {
                setLoading(false);
                return;
            }
        };

        fetchData();
    }, [shouldSearch, page, open, apiPath, label, query]);

    // Auto-fetch for agreement case
    const isAgreement = label === "agreement";
    useEffect(() => {
        if (!open || !isAgreement) return;

        if (isAgreement) {
            const fetchAgreementData = async () => {
                setLoading(true);
                try {
                    const res = await api.get(`${process.env.REACT_APP_URI}${apiPath}`);
                    if (res.data.success) {
                        const decrypted = decryptPayload(res.data.encryptedData);
                        setData(decrypted || []);
                    } else {
                        toast.error(res.data.message);
                    }
                } catch (err) {
                    console.error("Agreement fetch error:", err);
                    toast.error(err?.response?.data?.message || "Unable to fetch agreements");
                } finally {
                    setLoading(false);
                }
            };
            fetchAgreementData();
        }
    }, [open, apiPath, isAgreement]);

    const formatFieldValue = (field, value) => {
        if (field === "date") return formatDateTime(value);
        if (field === "agreementDate") return formatDateTime(value);
        if (field === "category") return formatLabel(value);
        return value || "-";
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-3">
            <div className="bg-white w-full max-w-xl rounded-2xl shadow-xl overflow-hidden">
                {/* Header */}
                <div className="px-5 py-4 border-b bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-t-xl">
                    <h3 className="text-center text-base sm:text-lg font-semibold tracking-wide">
                        Select {formatLabel(label)}
                    </h3>
                </div>

                {/* Search Section */}
                {!isAgreement && (
                    <div className="py-4 px-2">
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                placeholder="Search by name or phone..."
                                value={query}
                                onChange={(e) => {
                                    setQuery(e.target.value);
                                    setPage(1);
                                    setShouldSearch(false);
                                }}
                                className="flex-1 min-w-0 px-3 py-2 border rounded-lg outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                            />

                            <button
                                type="button"
                                onClick={() => {
                                    if (query.trim().length < 2) {
                                        toast.warning("Enter value to search");
                                        return;
                                    }
                                    setShouldSearch(true);
                                    setPage(1);
                                }}
                                className="flex items-center justify-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition active:scale-95"
                            >
                                <MagnifyingGlassIcon className="h-5 w-5" />
                            </button>
                        </div>
                    </div>
                )}

                {/* Multi Selected Preview */}
                {multi && selected.length > 0 && (
                    <div className="px-3 pb-2 flex flex-wrap gap-2">
                        {selected.map(item => (
                            <span
                                key={item._id}
                                className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full flex items-center gap-1"
                            >
                                {item.entryId || item.name}
                                <button
                                    onClick={() =>
                                        setSelected(prev => prev.filter(i => i._id !== item._id))
                                    }
                                    className="text-red-500 hover:text-red-700"
                                >
                                    ✕
                                </button>
                            </span>
                        ))}
                    </div>
                )}

                {/* Results */}
                <div className="max-h-[45vh] overflow-y-auto p-3 space-y-2">
                    {loading && (
                        <div className="flex justify-center items-center p-4">
                            <Loader />
                        </div>
                    )}

                    {!loading && !shouldSearch && data.length === 0 && (
                        <p className="text-sm text-gray-500 text-center py-4">
                            No data!
                        </p>
                    )}

                    {!loading && shouldSearch && data.length === 0 && (
                        <p className="text-sm text-gray-500 text-center py-4">
                            No results found
                        </p>
                    )}

                    {!loading &&
                        data.map((item) => {
                            const isSelected = selected.some(i => i._id === item._id);
                            return (
                                <div
                                    key={item._id}
                                    onClick={() => toggleSelect(item)}
                                    className={`p-2.5 border rounded-xl space-y-1 cursor-pointer transition
                                                ${isSelected ? "bg-blue-50 border-blue-500" : "hover:bg-gray-100"}
                                        `} >
                                    {displayFields.map((field) => (
                                        <div key={field} className="flex text-sm">
                                            {/* Label */}
                                            <span className="text-gray-500 font-medium">
                                                {formatLabel(field)}:{" "}
                                                <span className="text-gray-800">
                                                    {formatFieldValue(field, item[field])}
                                                </span>
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )
                        })}
                </div>

                {/* Pagination */}
                {!isAgreement && shouldSearch && pages > 1 && (
                    <div className="px-4 py-3 border-t flex items-center justify-between bg-gray-50">
                        <button
                            disabled={page <= 1}
                            onClick={() => setPage((p) => p - 1)}
                            className="px-2.5 py-1.5 text-xs sm:text-sm rounded-md bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
                        >
                            Prev
                        </button>

                        <span className="text-xs sm:text-sm text-gray-600">
                            Page {page} of {pages}
                        </span>

                        <button
                            disabled={page >= pages}
                            onClick={() => setPage((p) => p + 1)}
                            className="px-2.5 py-1.5 text-xs sm:text-sm rounded-md bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
                        >
                            Next
                        </button>
                    </div>
                )}

                {/* Footer */}
                <div className="p-3 border-t flex items-center justify-between">
                    <span className="text-sm text-gray-600">
                        {multi ? `Selected: ${selected.length}` : ""}
                    </span>

                    <button
                        onClick={() => {
                            if (multi) onSelect(selected);
                            onClose();
                        }}
                        className="px-3 py-2 text-sm rounded-lg bg-gray-200 hover:bg-gray-300"
                    >
                        {multi ? "Done" : "Close"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EntitySelector;